import React from 'react';
import { Home, Hotel, Building, Tent, Navigation } from 'lucide-react';

const AccommodationSection = ({ 
  formData, 
  handleInputChange, 
  errors, 
  accommodationTypes
}) => {
  // Функция для получения иконки типа проживания
  const getAccommodationIcon = (type) => {
    switch(type) {
      case 'hotel':
        return <Hotel className="w-5 h-5 text-purple-500" />;
      case 'hostel':
        return <Building className="w-5 h-5 text-emerald-500" />;
      case 'apartment':
        return <Home className="w-5 h-5 text-pink-500" />;
      case 'camping':
        return <Tent className="w-5 h-5 text-green-500" />;
      default:
        return <Hotel className="w-5 h-5 text-purple-500" />;
    }
  };

  // Получить текущий выбранный тип проживания
  const currentAccommodation = accommodationTypes.find(t => t.id === formData.accommodationType) || accommodationTypes[0];

  // Описания расположения жилья
  const locationDescriptions = {
    'city_center': 'В центре города с отличной доступностью к основным достопримечательностям, ресторанам и развлечениям. Обычно дороже.',
    'midtown': 'В срединной части города, хороший баланс между ценой и удобством расположения.',
    'outskirts': 'На окраине города, обычно дешевле, но требует больше времени на дорогу до основных достопримечательностей.'
  };

  return (
    <div className="mb-6 bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        {getAccommodationIcon(formData.accommodationType)}
        <span className="ml-2">Проживание</span>
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Тип проживания */}
        <div className="col-span-full">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Выберите тип проживания
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {accommodationTypes.map(type => (
              <label 
                key={type.id}
                className={`
                  flex flex-col items-center justify-center p-3 rounded-lg border
                  ${formData.accommodationType === type.id 
                    ? 'bg-purple-100 border-purple-300 text-purple-700 dark:bg-purple-900/40 dark:border-purple-700 dark:text-purple-300'
                    : 'bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-650'}
                  transition-colors cursor-pointer
                `}
              >
                <input
                  type="radio"
                  name="accommodationType"
                  value={type.id}
                  checked={formData.accommodationType === type.id}
                  onChange={handleInputChange}
                  className="sr-only"
                />
                <div className="mb-2">
                  {getAccommodationIcon(type.id)}
                </div>
                <span className="text-sm font-medium">{type.name}</span>
              </label>
            ))}
          </div>
          {errors.accommodationType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.accommodationType}
            </p>
          )}
        </div>

        {/* Расположение */}
        <div>
          <label htmlFor="accommodationLocation" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Navigation className="w-4 h-4 mr-2 text-purple-500" />
            Расположение
          </label>
          <select
            id="accommodationLocation"
            name="accommodationLocation"
            value={formData.accommodationLocation}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            <option value="city_center">Центр города</option>
            <option value="midtown">Средняя удаленность</option>
            <option value="outskirts">Окраина</option>
          </select>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            {locationDescriptions[formData.accommodationLocation]}
          </p>
        </div>

        {/* Рейтинг (для отелей) */}
        {formData.accommodationType === 'hotel' && (
          <div>
            <label htmlFor="accommodationRating" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Звездность отеля
            </label>
            <div className="flex items-center">
              <input
                type="range"
                id="accommodationRating"
                name="accommodationRating"
                min="1"
                max="5"
                step="1"
                value={formData.accommodationRating}
                onChange={handleInputChange}
                className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-purple-600"
              />
              <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                {formData.accommodationRating} ★
              </span>
            </div>
            {errors.accommodationRating && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.accommodationRating}
              </p>
            )}
          </div>
        )}

        {/* Завтрак */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              name="includeBreakfast"
              checked={formData.includeBreakfast}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Включить завтрак
            </span>
          </label>
        </div>
      </div>

      {/* Информация о выбранном типе проживания */}
      <div className="mt-4 bg-white dark:bg-gray-700 p-3 rounded-lg border border-purple-100 dark:border-purple-800">
        <div className="flex items-start">
          {getAccommodationIcon(formData.accommodationType)}
          <div className="ml-3">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
              {currentAccommodation.name}
            </h4>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {formData.accommodationType === 'hotel' && 'Классический вариант с полным спектром услуг. Удобство и сервис зависят от количества звезд.'}
              {formData.accommodationType === 'hostel' && 'Экономичный вариант с общими помещениями. Подходит для бюджетных путешественников и тех, кто хочет познакомиться с другими туристами.'}
              {formData.accommodationType === 'apartment' && 'Отдельное жилье с кухней. Идеально для длительного пребывания или семейных поездок. Больше пространства и приватности.'}
              {formData.accommodationType === 'camping' && 'Проживание на природе в палатках или кемпингах. Самый бюджетный вариант, подходит для любителей активного отдыха.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccommodationSection;